import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';
import TwoStepConfirmModal from '../components/ui/TwoStepConfirmModal';

const ConfirmContext = createContext(null);

const DEFAULTS = {
  title: 'İşlemi onayla',
  step1Description: 'Bu işlemi yapmak istediğinize emin misiniz?',
  step2Description:
    'Son onay: işlemi uygulamak için tekrar onaylayın. Bu adım geri alınamayabilir.',
  continueLabel: 'Devam',
  confirmLabel: 'Evet, Onayla',
  cancelLabel: 'İptal',
  tone: 'default',
  busyLabel: 'İşleniyor...',
};

/**
 * Uygulama genelinde 2 adımlı onay penceresi.
 * requestConfirm(options) → Promise<boolean>
 */
export const ConfirmProvider = ({ children }) => {
  const [request, setRequest] = useState(null);
  const [busy, setBusy] = useState(false);

  const close = useCallback((result) => {
    setBusy(false);
    setRequest((current) => {
      current?.resolve?.(result);
      return null;
    });
  }, []);

  const requestConfirm = useCallback((options = {}) => {
    if (request) {
      return Promise.resolve(false);
    }
    return new Promise((resolve) => {
      setRequest({ ...DEFAULTS, ...options, resolve });
    });
  }, [request]);

  const handleConfirm = useCallback(async () => {
    if (!request) return;
    const { onConfirm, resolve } = request;
    if (typeof onConfirm === 'function') {
      setBusy(true);
      try {
        const result = await onConfirm();
        setBusy(false);
        if (result === false) return;
        setRequest(null);
        resolve(true);
      } catch (err) {
        setBusy(false);
      }
      return;
    }
    close(true);
  }, [request, close]);

  const value = useMemo(() => ({ requestConfirm }), [requestConfirm]);

  return (
    <ConfirmContext.Provider value={value}>
      {children}
      <TwoStepConfirmModal
        isOpen={Boolean(request)}
        onClose={() => close(false)}
        onConfirm={handleConfirm}
        title={request?.title}
        step1Description={request?.step1Description}
        step2Description={request?.step2Description}
        continueLabel={request?.continueLabel}
        confirmLabel={request?.confirmLabel}
        cancelLabel={request?.cancelLabel}
        tone={request?.tone}
        busy={busy}
        busyLabel={request?.busyLabel}
        singleStep={Boolean(request?.singleStep)}
      />
    </ConfirmContext.Provider>
  );
};

export const useConfirm = () => {
  const ctx = useContext(ConfirmContext);
  if (!ctx) {
    throw new Error('useConfirm must be used inside ConfirmProvider');
  }
  return ctx.requestConfirm;
};
